import { type CalculatorDef, type CalcResult, checkFinite, err, num, ok } from './types';
import { ETHANOL_DENSITY } from './absoluteAlcohol';

/**
 * Grams of ethanol produced per gram of fermentable hexose, from the Gay-Lussac
 * stoichiometry of alcoholic fermentation:
 *
 *     C6H12O6  ->  2 C2H5OH  +  2 CO2
 *     180.16 g ->  2 x 46.07 g = 92.14 g
 *
 * Fruit sugars are overwhelmingly glucose and fructose — both hexoses, both 180.16 g/mol —
 * so this ratio is the right one for a fruit mash. Added sucrose would yield slightly more
 * per kilogram (it picks up a water molecule when it hydrolyses), which this does not model.
 */
export const ETHANOL_PER_SUGAR_MASS = 92.14 / 180.16;

/** Litres of pure ethanol per kg of fermentable sugar, at 100% conversion. */
export const LITRES_PER_KG_SUGAR = ETHANOL_PER_SUGAR_MASS / ETHANOL_DENSITY;

export type SugarYieldResult = {
  /** Fermentable sugar in the mash, kg. */
  sugarMass: number;
  /** Litres of pure ethanol expected after fermentation. */
  pureEthanol: number;
  /** What that becomes as finished spirit at 50 %ABV, litres. */
  spiritAt50: number;
  /** Theoretical maximum at 100% conversion, litres of pure ethanol. */
  theoreticalMax: number;
}

/**
 * Estimate how much alcohol a fruit mash will yield.
 *
 * Stoichiometry sets the ceiling — about {@link LITRES_PER_KG_SUGAR} litres of pure ethanol
 * per kilogram of sugar — and the efficiency figure accounts for everything real
 * fermentation and distillation lose: yeast consuming sugar for growth, unfermentable
 * residue, evaporation, alcohol left in the pot and discarded with heads and tails.
 *
 * **This is a planning estimate, not a promise.** The efficiency term is doing a lot of
 * work and swallows enormous variation between fruits, yeasts and stills. 85-90% is a fair
 * default for a careful small-scale run; a poor ferment can fall far below it. Measure your
 * actual output and calibrate the number to your own setup.
 *
 * @param mashMass     Total mass of mash, kg.
 * @param sugarContent Fermentable sugar as a percentage of that mass (% w/w).
 * @param efficiency   Fraction of the theoretical yield you actually recover, %.
 */
export function sugarYield(
  mashMass: number,
  sugarContent: number,
  efficiency: number,
): CalcResult<SugarYieldResult> {
  const invalid = checkFinite({ mashMass, sugarContent, efficiency });
  if (invalid) return { ok: false, error: invalid };

  if (mashMass <= 0) return err('error.massPositive', undefined, 'mashMass');
  if (sugarContent < 0 || sugarContent > 100) {
    return err('error.percentRange', undefined, 'sugarContent');
  }
  if (efficiency <= 0 || efficiency > 100) {
    return err('error.percentRange', undefined, 'efficiency');
  }

  const sugarMass = (mashMass * sugarContent) / 100;
  const theoreticalMax = sugarMass * LITRES_PER_KG_SUGAR;
  const pureEthanol = theoreticalMax * (efficiency / 100);

  return ok({
    sugarMass,
    pureEthanol,
    spiritAt50: pureEthanol * 2,
    theoreticalMax,
  });
}

export const sugarYieldCalculator: CalculatorDef = {
  id: 'sugarYield',
  notesKey: 'notes.sugarYield',
  fields: [
    { name: 'mashMass', unit: 'kilogram', min: 0.1, max: 100000, step: 1, defaultValue: 100 },
    { name: 'sugarContent', unit: 'percent', min: 0, max: 100, step: 0.1, defaultValue: 12 },
    { name: 'efficiency', unit: 'percent', min: 1, max: 100, step: 1, defaultValue: 85 },
  ],
  outputs: [
    { name: 'spiritAt50', unit: 'litre', precision: 1, primary: true },
    { name: 'pureEthanol', unit: 'litre', precision: 2 },
    { name: 'sugarMass', unit: 'kilogram', precision: 2 },
    { name: 'theoreticalMax', unit: 'litre', precision: 2 },
  ],
  compute: (i) => sugarYield(num(i, 'mashMass'), num(i, 'sugarContent'), num(i, 'efficiency')),
};
